import { useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectBillingInfo } from '@app/redux/billingInfo';
import { parseDateToUtc } from '@app/utils';

import { Invoice as InvoiceType, Payment, SettlementStatus } from '@mfe/shared/schema-types';

import { InvoiceStatusChip } from '../../invoice-status-chip';
import { Boleto } from '../boleto';
import Links from './links';
import InvoiceMenu from './menu';

import sharedStyles from '../../../styles.module.scss';
import styles from './styles.module.scss';

type InvoiceProps = {
  invoice: InvoiceType;
  payment?: Payment | null;
  index: number;
};

const Invoice = ({ invoice, payment, index }: InvoiceProps): JSX.Element => {
  const invoiceAmount = invoice?.invoiceAmount?.value || 0;
  const showBoleto =
    invoice?.invoiceAmount?.alphabeticCode === 'BRL' &&
    invoice?.invoiceSettlementStatus !== SettlementStatus.Paid &&
    invoiceAmount > 0;

  const {
    billingInfo: { popupsBlocked },
  } = useSelector(selectBillingInfo);

  const showDetails = invoice?.dueDate && invoice?.invoiceAmount?.text;

  return (
    <div className={styles['statementContainer']}>
      <div className={styles['statementRowContainer']}>
        <Header invoice={invoice} />
      </div>

      {showDetails && (
        <div className={styles['statementRowContainer']} id="statements">
          <Body invoice={invoice} />
        </div>
      )}

      {showBoleto && (
        <div className={styles['statementRowContainer']}>
          <Boleto invoice={invoice} payment={payment} showLabel />
        </div>
      )}

      <Links invoice={invoice} index={index} showPopupMessage={popupsBlocked ?? false} />
    </div>
  );
};

const Header = ({ invoice }: { invoice: InvoiceType }): JSX.Element => {
  const { t } = useTranslation('BillingStatement');
  const statementDate = invoice?.statementDate ? parseDateToUtc(invoice.statementDate) : undefined;

  return (
    <div className={styles['headerContainer']}>
      <div className={styles['header']}>
        <Txt variant="paragraphBold" style={{ textTransform: 'capitalize' }}>
          {t('statementDate', { statementDate })}
        </Txt>

        {invoice?.badgeStatus && <InvoiceStatusChip badgeStatus={invoice.badgeStatus} />}
      </div>

      <InvoiceMenu invoice={invoice} />
    </div>
  );
};

const Body = ({ invoice }: { invoice: InvoiceType }): JSX.Element => {
  const { t } = useTranslation('BillingStatement');

  const dueDate = invoice?.dueDate ? parseDateToUtc(invoice.dueDate) : undefined;

  return (
    <div className={styles['body']}>
      <div className={sharedStyles['flexColumn']}>
        <Txt component="p" variant="labelMedium" pb="8px">
          {t('dueDate')}
        </Txt>
        <Txt component="p" variant="bodySmallRegular" data-cy="dueDate" style={{ textTransform: 'capitalize' }}>
          {t('dueDate_format', { dueDate })}
        </Txt>
      </div>

      <div className={sharedStyles['flexColumn']} style={{ textAlign: 'right' }}>
        <Txt component="p" variant="labelMedium" pb="8px">
          {t('amountDue')}
        </Txt>
        <Txt component="p" variant="bodySmallRegular">
          {invoice.invoiceAmount?.text}
        </Txt>
      </div>
    </div>
  );
};

export default Invoice;
